import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "/brand/guleed-logo-dark.png";

export default function Navbar() {
  const { lang, toggleLang, t } = useLanguage();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  const navLinks = [
    { to: '/', label: t('nav', 'home') },
    { to: '/about', label: t('nav', 'about') },
    { to: '/services', label: t('nav', 'services') },
    { to: '/digital-assets', label: lang === 'ar' ? 'العملات الرقمية' : 'Crypto to Property' },
    { to: '/rates', label: t('nav', 'rates') },
    { to: '/blog', label: lang === 'ar' ? 'المدونة' : 'Blog' },
    { to: '/referral', label: lang === 'ar' ? 'برنامج الإحالة' : 'Referral Program' },
    { to: '/contact', label: t('nav', 'contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 gmre-glass-nav ${scrolled ? 'scrolled' : ''}`}
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Guleed Mortgage" className="h-10 lg:h-12" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive(link.to)
                    ? 'text-[#C9A84C] bg-white/10'
                    : 'text-white/80 hover:text-white hover:bg-white/5'
                }`}
                style={{ fontFamily: lang === 'ar' ? 'var(--font-ar-body)' : 'var(--font-en-body)' }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right section */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Lang Toggle */}
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white/80 hover:text-white border border-white/20 rounded-lg transition-colors hover:border-white/40"
            >
              <Globe className="w-4 h-4" />
              <span>{lang === 'en' ? 'عر' : 'EN'}</span>
            </button>

            {user && user !== false ? (
              <div className="flex items-center gap-3">
                <Link to="/portal">
                  <Button
                    data-testid="nav-portal-btn"
                    variant="outline"
                    className="border-[#A68966] text-[#A68966] hover:bg-[#A68966]/10 text-sm"
                  >
                    {t('nav', 'portal')}
                  </Button>
                </Link>
                {user.role === 'admin' && (
                  <Link to="/admin">
                    <Button data-testid="nav-admin-btn" variant="outline" className="border-[#C9A84C] text-[#C9A84C] hover:bg-[#C9A84C]/10 text-sm">
                      {lang === 'ar' ? 'الإدارة' : 'Admin'}
                    </Button>
                  </Link>
                )}
                <Button
                  data-testid="nav-logout-btn"
                  onClick={logout}
                  variant="ghost"
                  className="text-white/70 hover:text-white text-sm"
                >
                  {t('nav', 'logout')}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button
                    data-testid="nav-login-btn"
                    variant="ghost"
                    className="text-white/80 hover:text-white text-sm"
                  >
                    {t('nav', 'login')}
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    data-testid="nav-cta-btn"
                    className="bg-[#C9A84C] hover:bg-[#A68966] text-[#0B2F44] font-bold text-sm px-5"
                  >
                    {t('nav', 'cta')}
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden gmre-glass-nav scrolled border-t border-[#A68966]/15" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-4 py-3 text-sm tracking-wide gmre-ios-transition ${
                  isActive(link.to) ? 'text-[#C9A84C] gmre-glass-pill' : 'text-white/80 hover:text-white hover:bg-white/5 rounded-full'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 mt-2 border-t border-white/10 space-y-2">
              <button onClick={toggleLang} className="flex items-center gap-2 px-4 py-3 text-white/80 hover:text-white w-full">
                <Globe className="w-4 h-4" /> {lang === 'en' ? 'العربية' : 'English'}
              </button>
              {user && user !== false ? (
                <>
                  <Link to="/portal" className="block px-4 py-3 text-[#C9A84C] hover:bg-white/5 rounded-lg">{t('nav', 'portal')}</Link>
                  <button onClick={logout} className="block px-4 py-3 text-white/60 hover:text-white w-full text-start">{t('nav', 'logout')}</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="block px-4 py-3 text-white/80 hover:text-white rounded-lg">{t('nav', 'login')}</Link>
                  <Link to="/contact" className="block">
                    <Button className="gmre-btn-sheen w-full bg-[#C9A84C] hover:bg-[#A68966] text-[#0B2F44] font-semibold tracking-[0.14em] uppercase text-sm">{t('nav', 'cta')}</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
